import type React from "react";
import styled, { css } from "styled-components";

// Define the possible HTML elements
type Element =
	| "h1"
	| "h2"
	| "h3"
	| "h4"
	| "h5"
	| "h6"
	| "p"
	| "span"
	| "div"
	| "a"
	| "li";

export declare type Variant =
	| "h1"
	| "h2"
	| "h3"
	| "h4"
	| "h5"
	| "h6"
	| "p"
	| "textXSSM"
	| "textXSM"
	| "textSM"
	| "textMD"
	| "textLG"
	| "textXL"
	| "textXXL"
	| "paragraphMono"
	| "paragraphXSM"
	| "paragraphSM"
	| "paragraphMD"
	| "paragraphXL"
	| "paragraphLG";

// Define the props for the Typography component
type TypographyProps = {
	variant: Variant;
	as?: Element;
	color?: string;
	margin?: string;
	padding?: string;
	textAlign?: "left" | "right" | "center" | "justify";
	fontOverride?: "space-grotesk" | "space-mono" | "inter";
	children: React.ReactNode;
	fontWeight?: string | number;
	disableMobileSizes?: boolean;
};

type StyledTypographyProps = {
	$variant: Variant;
	$color?: string;
	$margin?: string;
	$padding?: string;
	$textAlign?: "left" | "right" | "center" | "justify";
	$fontOverride?: "space-grotesk" | "space-mono" | "inter";
	$fontWeight?: string | number;
	$disableMobileSizes?: boolean;
};

// Styled-component for Typography
const StyledTypography = styled.span<StyledTypographyProps>`
	${({
		$variant,
		$color,
		$margin,
		$padding,
		$textAlign,
		$fontOverride,
		$fontWeight,
		$disableMobileSizes,
	}) => css`
		margin: ${$margin || "0"};
		padding: ${$padding || "0"};
		text-align: ${$textAlign || "inherit"};
		font-family: "Inter", sans-serif;

		${
			$variant === "h1" &&
			css`
			font-family: "Space Grotesk", sans-serif;
			font-size: ${$disableMobileSizes ? "3.75rem" : "2rem"};
			line-height: ${$disableMobileSizes ? "5rem" : "3rem"};
			font-weight: 500;
			color: var(--text-heading-title);
			@media (min-width: 1024px) {
				font-size: 3.75rem;
				line-height: 5rem;
			}
		`
		}

		${
			$variant === "h2" &&
			css`
			font-family: "Space Grotesk", sans-serif;
			font-size: ${$disableMobileSizes ? "3rem" : "1.5rem"};
			line-height: ${$disableMobileSizes ? "3.75rem" : "2rem"};
			font-weight: 500;
			color: var(--text-heading-title);

			@media (min-width: 1024px) {
				font-size: 3rem;
				line-height: 3.75rem;
			}
		`
		}

    ${
			$variant === "h3" &&
			css`
			font-family: "Space Grotesk", sans-serif;
			font-size: ${$disableMobileSizes ? "2.5rem" : "1.5rem"};
			line-height: ${$disableMobileSizes ? "3.25rem" : "2rem"};
			font-weight: 500;
			color: var(--text-heading-title);

			@media (min-width: 1024px) {
				font-size: 2.5rem;
				line-height: 3.25rem;
			}
		`
		}

    ${
			$variant === "h4" &&
			css`
			font-family: "Space Grotesk", sans-serif;
			font-size: ${$disableMobileSizes ? "2.25rem" : "1.5rem"};
			line-height: ${$disableMobileSizes ? "3rem" : "2rem"};
			font-weight: 500;
			color: var(--text-heading-title);

			@media (min-width: 1024px) {
				font-size: 2.25rem;
				line-height: 3rem;
			}
		`
		}

    ${
			$variant === "h5" &&
			css`
			font-family: "Space Grotesk", sans-serif;
			font-size: 1.5rem;
			line-height: 2rem;
			font-weight: 500;
			color: var(--text-heading-title);
		`
		}

    ${
			$variant === "h6" &&
			css`
			font-family: "Space Grotesk", sans-serif;
			font-size: 1.25rem;
			line-height: 1.75rem;
			font-weight: 500;
			color: var(--text-heading-title);
		`
		}

    ${
			$variant === "p" &&
			css`
			font-family: "Inter", sans-serif;
			font-size: 1rem;
			line-height: 1.25rem;
			font-weight: 400;
			color: var(--on-surface);
		`
		}

    ${
			$variant === "textXSSM" &&
			css`
			font-size: 0.625rem;
			line-height: 0.75rem;
		`
		}

    ${
			$variant === "textXSM" &&
			css`
			font-size: 0.625rem;
			line-height: 0.75rem;
		`
		}

    ${
			$variant === "textSM" &&
			css`
			font-size: 0.875rem;
			line-height: 1.25rem;
		`
		}

    ${
			$variant === "textMD" &&
			css`
			font-size: 1rem;
			line-height: 1.25rem;
		`
		}

    ${
			$variant === "textLG" &&
			css`
			font-size: 1.125rem;
			line-height: 1.5rem;
		`
		}

    ${
			$variant === "textXL" &&
			css`
			font-size: 1.25rem;
			line-height: 1.75rem;
		`
		}

    ${
			$variant === "textXXL" &&
			css`
			font-size: 1.5rem;
			line-height: 2rem;
		`
		}

    ${
			$variant === "paragraphMono" &&
			css`
			font-family: "Space Mono", sans-serif;
			font-size: 16px;
			line-height: 20px;
			font-weight: 400;
			color: var(--on-surface);
		`
		}

	${
		$variant === "paragraphXSM" &&
		css`
			font-family: "Inter", sans-serif;
			font-size: 12px;
			line-height: 16px;
			font-weight: 500;
		`
	}

    ${
			$variant === "paragraphSM" &&
			css`
			font-family: "Inter", sans-serif;
			font-size: 14px;
			line-height: 20px;
			font-weight: 400;
			text-align: left;
		`
		}

    ${
			$variant === "paragraphMD" &&
			css`
			font-family: "Inter", sans-serif;
			font-size: 16px;
			line-height: 20px;
			font-weight: 400;
			color: var(--on-surface);
		`
		}

	${
		$variant === "paragraphLG" &&
		css`
			font-family: "Inter", sans-serif;
			font-size: 18px;
			line-height: 24px;
			font-weight: 400;
	`
	}

    ${
			$variant === "paragraphXL" &&
			css`
			font-family: "Inter", sans-serif;
			font-size: 20px;
			line-height: 28px;
			font-weight: 500;
		`
		}

		${
			$fontOverride === "space-grotesk" &&
			css`
			font-family: "Space Grotesk", sans-serif;
		`
		}

		${
			$fontOverride === "space-mono" &&
			css`
			font-family: "Space Mono", sans-serif;
		`
		}

		${
			$fontOverride === "inter" &&
			css`
			font-family: "Inter", sans-serif;
		`
		}

		${
			$color &&
			css`
			color: ${$color};
		`
		}

		${
			$fontWeight &&
			css`
			font-weight: ${$fontWeight};
		`
		}
	`}
`;

export const Typography: React.FC<TypographyProps> = ({
	variant,
	as,
	color,
	margin,
	padding,
	textAlign,
	children,
	fontOverride,
	fontWeight,
	disableMobileSizes,
	...rest
}) => {
	const asComponent = as
		? as
		: [
					"h1",
					"h2",
					"h3",
					"h4",
					"h5",
					"h6",
					"p",
					"span",
					"div",
					"a",
					"li",
				].includes(variant)
			? variant
			: "div";

	return (
		<StyledTypography
			as={asComponent}
			$variant={variant}
			$color={color}
			$margin={margin}
			$padding={padding}
			$textAlign={textAlign}
			$fontOverride={fontOverride}
			$fontWeight={fontWeight}
			$disableMobileSizes={disableMobileSizes}
			{...rest}
		>
			{children}
		</StyledTypography>
	);
};
